import React from 'react';
import { Box, Text, HStack, Input, Button, Icon, Link } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiSend, FiCheckCircle } from 'react-icons/fi';
import useAppToast from 'components/toast';
import { useDispatch } from 'react-redux';
import {
	metaWhatsappApi,
	useStartConversationMutation,
	useSendMessageMutation,
} from 'api/metaWhatsappApi';

/**
 * Prove a connected number actually works, from this screen.
 *
 * ★ WHY `hello_world` AND NOTHING ELSE — STILL, NOW THE INBOX HAS A PICKER.
 * A number that has never had an inbound message can only be written to with an
 * approved template; a plain text send is refused by Meta with an error about
 * the 24-hour window, which reads like a broken setup. `hello_world` is Meta's
 * universally pre-approved, parameterless template, so it is the one message
 * every workspace can send on day one — which is exactly the day this button is
 * pressed.
 *
 * The inbox's picker is deliberately NOT reused here, for two reasons. It is
 * scoped to a conversation, and this screen has none until `startConversation`
 * has already run; and a connectivity probe should not depend on the workspace
 * having approved templates of its own, because "can this number send at all"
 * is the question being asked. Choosing a template is the agent's job in a real
 * thread, not an administrator's while proving the wiring.
 *
 * ★ NO NEW BACKEND SURFACE. It walks the two endpoints the inbox already uses
 * — start a conversation, then post a message to it — so a successful test
 * proves the real send path, permissions included, rather than a special one
 * that only exists for testing.
 *
 * ★ WHY IT IS GATED ON ASSIGNMENT.
 * Sending resolves the sender from the caller's OWN eligible channels, and a
 * channel is eligible only for someone assigned to it. An administrator who
 * assigned other people but not themselves gets NO_ELIGIBLE_CHANNEL — which is
 * correct, and says so in words rather than as a raw code.
 */

/**
 * Meta's pre-approved template, and the only one guaranteed to exist.
 *
 * The server validates this against the mirrored catalogue like any other
 * template, and syncs the account once if it has never been read — so a
 * workspace that connected a number a minute ago can still press this.
 */
const HELLO_WORLD = { name: 'hello_world', language: { code: 'en_US' } };

const START_ERRORS = {
	NO_ELIGIBLE_CHANNEL:
		'You are not assigned to this number yourself, so you cannot send from it. Add yourself above and try again.',
	CHANNEL_NOT_ELIGIBLE:
		'You are not assigned to this number yourself, so you cannot send from it. Add yourself above and try again.',
	RECIPIENT_REQUIRED: 'Enter a number to send the test to.',
	RECIPIENT_INVALID: 'That is not a valid phone number for this country.',
	RECIPIENT_IS_OWN_NUMBER: 'That is this workspace’s own WhatsApp number — Meta will not deliver to it.',
	PHONE_AMBIGUOUS: 'That number could be read two ways. Type it with a country code, e.g. +971…',
	FORBIDDEN: 'Your role does not allow starting a conversation.',
	CHANNEL_NOT_SENDABLE: 'Meta will not accept sends from this number yet.',
	SEND_UNAVAILABLE: 'The WhatsApp connection service could not be reached.',
	PROVIDER_ERROR: 'Meta refused the message. Check the number and the account’s status.',
};

const explain = (payload = {}) =>
	START_ERRORS[payload.error ?? payload.reason] ??
	payload.message ??
	'The test message could not be sent.';

export default function TestSend({ channel }) {
	const notify = useAppToast();
	const dispatch = useDispatch();
	const [to, setTo] = React.useState('');
	const [sentTo, setSentTo] = React.useState(null);
	const [startConversation, { isLoading: starting }] = useStartConversationMutation();
	const [sendMessage, { isLoading: sending }] = useSendMessageMutation();

	const assigned = (channel.assignedUsers ?? 0) > 0;
	const busy = starting || sending;

	const send = async () => {
		const recipient = to.trim();
		if (!recipient) {
			notify.error('Nothing sent', START_ERRORS.RECIPIENT_REQUIRED);
			return;
		}

		try {
			// The sender is named explicitly: this button belongs to ONE card, and
			// letting the server pick would make the test prove the wrong number.
			const started = await startConversation({
				to: recipient,
				channelPhoneNumberId: channel.phoneNumberId,
			}).unwrap();

			await sendMessage({
				conversationId: started.conversationId,
				type: 'template',
				template: HELLO_WORLD,
				// The client id is the idempotency key, so a double-click or a retry
				// after a timeout cannot send the customer two messages.
				clientMessageId: `test_${channel.phoneNumberId}_${Date.now()}`,
			}).unwrap();

			setSentTo(recipient);
			// ★ THE STRIP READS `lastOutboundAt`, WHICH THE SERVER OWNS.
			// A send invalidates conversations, not the settings read, so without
			// this the "Test message sent" step stays grey until the page reloads --
			// the one moment the progress strip most needs to move. Invalidated here
			// rather than on the mutation itself, so an ordinary inbox reply does not
			// refetch the whole settings screen.
			dispatch(metaWhatsappApi.util.invalidateTags(['Settings']));
			notify.success('Test sent', `A test template went to ${recipient}.`);
		} catch (err) {
			setSentTo(null);
			notify.error('Not sent', explain(err?.data ?? {}));
		}
	};

	return (
		<Box>
			<Text fontSize="sm" color="text.primary">
				Send a test message
			</Text>
			<Text fontSize="xs" color="text.muted" mb="2">
				{assigned
					? 'Sends Meta’s standard test template to a number you choose, through the real send path.'
					: 'Assign someone to this number first — a number nobody is on cannot send.'}
			</Text>

			<HStack spacing="2">
				<Input
					size="sm"
					bg="bg.input"
					placeholder="+971 50 123 4567"
					value={to}
					onChange={(e) => setTo(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !busy && assigned) send();
					}}
					isDisabled={!assigned}
					aria-label="Number to send the test message to"
				/>
				<Button
					size="sm"
					leftIcon={<Icon as={FiSend} />}
					onClick={send}
					isLoading={busy}
					isDisabled={!assigned || !to.trim()}
				>
					Send test
				</Button>
			</HStack>

			{sentTo && (
				<HStack spacing="2" mt="2">
					<Icon as={FiCheckCircle} color="success" />
					<Text fontSize="xs" color="text.secondary">
						Sent to {sentTo}. It is in{' '}
						<Link as={RouterLink} to="/meta-whatsapp" color="text.accent" textDecoration="underline">
							the inbox
						</Link>
						{' '}— a delivery tick there means Meta accepted it.
					</Text>
				</HStack>
			)}
		</Box>
	);
}
